import { createFileRoute, redirect, Outlet, useRouterState, useNavigate } from '@tanstack/react-router'
import { useCallback, Suspense } from 'react'
import { useAuthStore } from '../stores/authStore'
import { getCurrentUser, logout as apiLogout } from '../lib/auth'
import { AppLayout } from '../components/layout/AppLayout'
import { AlertToastProvider } from '../features/alerts/components/AlertToast'
import { ErrorBoundary } from '../components/ErrorBoundary'

/**
 * Skeleton fallback rendered while lazy-loaded route components are loading.
 * Provides a visual placeholder that mimics a typical page layout.
 */
function RouteSkeleton() {
  return (
    <div className="animate-pulse space-y-6" data-testid="route-skeleton">
      {/* Title bar skeleton */}
      <div className="flex items-center justify-between">
        <div className="h-7 w-48 rounded bg-[var(--color-bg-tertiary)]" />
        <div className="h-9 w-28 rounded bg-[var(--color-bg-tertiary)]" />
      </div>
      {/* Content skeleton rows */}
      <div className="space-y-4">
        <div className="h-4 w-full rounded bg-[var(--color-bg-tertiary)]" />
        <div className="h-4 w-5/6 rounded bg-[var(--color-bg-tertiary)]" />
        <div className="h-4 w-4/6 rounded bg-[var(--color-bg-tertiary)]" />
      </div>
      {/* Card grid skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-32 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)]"
          />
        ))}
      </div>
      {/* Table skeleton */}
      <div className="space-y-3">
        <div className="h-10 w-full rounded bg-[var(--color-bg-tertiary)]" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 w-full rounded bg-[var(--color-bg-secondary)]" />
        ))}
      </div>
    </div>
  )
}

/**
 * Pathless layout route that protects all child routes via beforeLoad.
 *
 * Any route file placed under `_authenticated/` will require the user
 * to be authenticated before it loads. Unauthenticated users are
 * redirected to /login with a `redirect` search param.
 *
 * @example
 * File: src/routes/_authenticated/dashboard.tsx
 * URL:  /dashboard (protected by beforeLoad)
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    const state = useAuthStore.getState()

    // If the auth check hasn't completed yet, perform it directly here
    // to avoid a deadlock (useAuth's useEffect only fires after component mount,
    // but beforeLoad blocks component mounting).
    if (state.isLoading) {
      try {
        const user = await getCurrentUser()
        useAuthStore.getState().setUser({
          id: user.id,
          email: user.email,
          name: user.name,
          role: user.role,
        })
      } catch {
        useAuthStore.getState().setUser(null)
      }
    }

    const { isAuthenticated } = useAuthStore.getState()
    if (!isAuthenticated) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.pathname !== '/' ? location.pathname : undefined,
        },
      })
    }
  },
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const user = useAuthStore((s) => s.user)
  const storeLogout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const activePath = useRouterState({ select: (s) => s.location.pathname })

  const onLogout = useCallback(async () => {
    try {
      await apiLogout()
    } catch {
      // Even if the API call fails, clear local state
    }
    storeLogout()
    navigate({ to: '/login' })
  }, [storeLogout, navigate])

  return (
    <>
      <AlertToastProvider />
      <AppLayout
        activePath={activePath}
        userName={user?.name}
        userAvatar={undefined}
        userRole={user?.role}
        onLogout={onLogout}
      >
        <ErrorBoundary>
          <Suspense fallback={<RouteSkeleton />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </AppLayout>
    </>
  )
}
